import { hasJoinedWaitlist } from '../../utils/waitlistJoined';

/**
 * Whether the join-the-waitlist popup may show. Plain TypeScript, no React and
 * no DOM: storage is passed in so the rules test with an in-memory fake.
 *
 * - Once someone has joined (through any form, see waitlistJoined) it never
 *   shows again.
 * - A dismissal hides it for 24 hours.
 * - It never shows on the waitlist journey, the legal pages or enrolment.
 * - Anything unreadable means "do not show". Nagging is the worse failure.
 */
export const POPUP_STORAGE_KEY = 'melio_waitlist_popup';
export const DISMISS_MS = 24 * 60 * 60 * 1000;

export interface PopupStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

/** Each section and everything under it. */
const EXCLUDED_SECTIONS = ['/waitlist', '/terms', '/privacy', '/enrollment', '/book', '/join'];

/** Case-insensitive, because react-router matches routes case-insensitively. */
export function isExcludedPath(path: string): boolean {
  const lower = path.toLowerCase();
  return EXCLUDED_SECTIONS.some((section) => lower === section || lower.startsWith(`${section}/`));
}

/** localStorage, or null where it cannot be reached (private mode, blocked). */
export function browserStorage(): PopupStorage | null {
  try {
    return typeof window === 'undefined' ? null : window.localStorage;
  } catch {
    return null;
  }
}

function readState(storage: PopupStorage): { state?: unknown; until?: unknown } | 'unreadable' | null {
  try {
    const raw = storage.getItem(POPUP_STORAGE_KEY);
    if (raw == null) return null;
    const parsed: unknown = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? (parsed as { state?: unknown; until?: unknown }) : 'unreadable';
  } catch {
    return 'unreadable';
  }
}

export function canShowPopup(path: string, now: number, storage: PopupStorage | null): boolean {
  if (isExcludedPath(path) || !storage) return false;
  try {
    if (hasJoinedWaitlist(storage)) return false;
  } catch {
    return false;
  }
  const stored = readState(storage);
  if (stored === null) return true;
  if (stored === 'unreadable') return false;
  if (stored.state === 'dismissed' && typeof stored.until === 'string') {
    const until = Date.parse(stored.until);
    return Number.isFinite(until) && now >= until;
  }
  return false;
}

function write(storage: PopupStorage | null, value: object): void {
  if (!storage) return;
  try {
    storage.setItem(POPUP_STORAGE_KEY, JSON.stringify(value));
  } catch {
    // Storage blocked: the popup simply may show again next visit.
  }
}

export function recordDismissed(now: number, storage: PopupStorage | null): void {
  write(storage, { state: 'dismissed', until: new Date(now + DISMISS_MS).toISOString() });
}
